import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useDispatch } from "react-redux";
import { useHistory, useParams } from "react-router-dom";
import LandingRenderer from "../../components/LandingRenderer";
import { LANDING_CSS } from "../../components/landingCss";
import {
  FiArrowUp,
  FiArrowDown,
  FiPlus,
  FiTrash,
  FiX,
  FiEye,
  FiChevronDown,
  FiChevronUp,
  FiMove,
  FiFileText,
  FiType,
  FiAlignLeft,
  FiImage,
  FiMessageSquare,
  FiMousePointer,
  FiList,
} from "react-icons/fi";
import swal from "@sweetalert/with-react";
import Modal from "../../components/Modal";
import TextInput from "../../components/TextInput";
import Textarea from "../../components/Textarea";
import Editor from "../../components/Editor";
import Loader from "../../components/Loader";
import BigLoader from "../../components/BigLoader";
import Button from "../../components/Button";
import { setInterface } from "../../redux/slices/interfaceSlice";
import service from "../../service";
import { frontendUrl } from "../../utils/landing";

const money = (value) =>
  new Intl.NumberFormat("id-ID", {
    style: "currency",
    currency: "IDR",
    maximumFractionDigits: 0,
  }).format(Number(value) || 0);

const elementTypes = [
  { value: "heading", label: "Heading", icon: FiType },
  { value: "paragraph", label: "Paragraph", icon: FiAlignLeft },
  { value: "image", label: "Image", icon: FiImage },
  { value: "testimonials", label: "Testimonials", icon: FiMessageSquare },
  { value: "button", label: "Button", icon: FiMousePointer },
  { value: "list", label: "List", icon: FiList },
];

const elementTypeLabel = (type) =>
  elementTypes.find((t) => t.value === type)?.label || type;

const stripHtml = (html) =>
  String(html || "")
    .replace(/<[^>]+>/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const buttonStyles = [
  { value: "dark", label: "Dark (default)" },
  { value: "primary", label: "Primary" },
  { value: "outline", label: "Outline" },
];

const capiEvents = [
  { value: "", label: "Disabled" },
  { value: "PageView", label: "PageView" },
  { value: "ViewContent", label: "ViewContent" },
  { value: "Lead", label: "Lead" },
  { value: "InitiateCheckout", label: "InitiateCheckout" },
  { value: "AddPaymentInfo", label: "AddPaymentInfo" },
];

const LANDING_WIDTH = 800;

const iconBtnClass =
  "flex items-center justify-center rounded-sm text-black-300 hover:bg-white-200 hover:text-black-200 transition-colors disabled:opacity-30 disabled:pointer-events-none";
const dangerIconBtnClass =
  "flex items-center justify-center rounded-sm text-red-300 hover:bg-red-50 transition-colors";

const defaultElement = (type) => {
  switch (type) {
    case "heading":
      return { type, level: 2, text: "Heading Baru" };
    case "paragraph":
      return { type, content: "<p>Paragraf baru</p>" };
    case "image":
      return { type, src: "", alt: "", caption: "" };
    case "testimonials":
      return { type, items: [{ name: "", text: "" }] };
    case "button":
      return { type, label: "Klik Disini", url: "#checkout", style: "dark" };
    case "list":
      return { type, ordered: false, items: [{ text: "" }] };
    default:
      return { type: "paragraph", content: "" };
  }
};

export default function ComposeLandingPage({ editMode = false }) {
  const dispatch = useDispatch();
  const { id } = useParams();
  const history = useHistory();
  const [loading, setLoading] = useState(false);
  const [loadingContent, setLoadingContent] = useState(editMode);
  const [name, setName] = useState("");
  const [title, setTitle] = useState("");
  const [slug, setSlug] = useState("");
  const [status, setStatus] = useState("draft");
  const [elements, setElements] = useState([]);
  const [elementKeys, setElementKeys] = useState([]);
  const [expandedKeys, setExpandedKeys] = useState(() => new Set());
  const [dragIndex, setDragIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const [checkoutConfig, setCheckoutConfig] = useState({
    courses: [],
    deep_reads: [],
    button_text: "Lanjutkan Pembayaran",
    checkout_event: "AddPaymentInfo",
    page_view_event: "ViewContent",
  });
  const [courses, setCourses] = useState([]);
  const [deepReads, setDeepReads] = useState([]);
  const [activeTab, setActiveTab] = useState("elements");
  const [publishMenuOpen, setPublishMenuOpen] = useState(false);
  const addModalRef = useRef(null);
  const nextKeyRef = useRef(0);
  const newKey = () => `el-${nextKeyRef.current++}`;

  const courseOptions = useMemo(
    () =>
      courses.map((c) => ({
        value: c.id,
        label: `${c.title}${c.price != null ? ` — ${money(c.price)}` : ""}`,
        type: "course",
      })),
    [courses]
  );

  const deepReadOptions = useMemo(
    () =>
      deepReads.map((d) => ({
        value: d.id,
        label: `${d.title}${d.price != null ? ` — ${money(d.price)}` : ""}`,
        type: "deep_read",
      })),
    [deepReads]
  );

  const getCourses = () => {
    service
      .get("/api/course", { params: { page: 1 } })
      .then((response) => {
        setCourses(response.data.data || response.data || []);
      })
      .catch(() => setCourses([]));
  };

  const getDeepReads = () => {
    service
      .get("/api/deep-read", { params: { page: 1 } })
      .then((response) => {
        setDeepReads(response.data.data || response.data || []);
      })
      .catch(() => setDeepReads([]));
  };

  const getOptionValues = (ids, options, type) =>
    options
      .filter((o) => ids.map(String).includes(String(o.value)))
      .map((o) => ({ ...o, type }));

  const buildCheckoutItems = () => {
    const items = [];
    checkoutConfig.courses.forEach((cid) => {
      const course = courses.find((c) => c.id === cid);
      if (course)
        items.push({
          id: course.id,
          type: "course",
          name: course.title,
          title: course.title,
          price: course.price,
        });
    });
    checkoutConfig.deep_reads.forEach((did) => {
      const dr = deepReads.find((d) => d.id === did);
      if (dr)
        items.push({
          id: dr.id,
          type: "deep_read",
          name: dr.title,
          title: dr.title,
          price: dr.price,
        });
    });
    return items;
  };

  const previewPage = useMemo(() => {
    return {
      name,
      title,
      slug,
      status,
      width: LANDING_WIDTH,
      elements,
      checkoutConfig: { ...checkoutConfig, items: buildCheckoutItems() },
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [name, title, slug, status, elements, checkoutConfig, courses, deepReads]);

  const save = (overrideStatus) => {
    if (!name.trim()) {
      swal("Error", "Nama tidak boleh kosong", "error");
      setActiveTab("page");
      return;
    }
    if (!slug.trim()) {
      swal("Error", "Slug tidak boleh kosong", "error");
      setActiveTab("page");
      return;
    }
    setLoading(true);
    const payload = {
      name,
      title,
      slug,
      status: overrideStatus || status,
      width: LANDING_WIDTH,
      elements,
      checkoutConfig,
    };
    const req = editMode
      ? service.put(`/api/landing-pages/${id}`, payload)
      : service.post("/api/landing-pages", payload);
    req
      .then((response) => {
        setLoading(false);
        if (overrideStatus) setStatus(overrideStatus);
        swal(
          "Berhasil",
          editMode ? "Landing page berhasil disimpan" : "Landing page berhasil ditambah",
          "success"
        ).then(() => {
          if (!editMode && response.data?.id) {
            history.replace(`/landing-page/edit/${response.data.id}`);
          }
        });
      })
      .catch((e) => {
        setLoading(false);
        const msg =
          e?.response?.data?.message ||
          e?.response?.data?.errors?.[0]?.message ||
          "Gagal menyimpan. Coba lagi.";
        swal("Gagal", String(msg), "error");
      });
  };

  const loadPage = () => {
    service
      .get(`/api/landing-pages/${id}`)
      .then((response) => {
        const d = response.data;
        setLoadingContent(false);
        setName(d.name || "");
        setTitle(d.title || "");
        setSlug(d.slug || "");
        setStatus(d.status || "draft");
        const loadedElements = Array.isArray(d.elements)
          ? d.elements.filter((element) => element && typeof element === "object" && element.type !== "checkoutform")
          : [];
        setElements(loadedElements);
        setElementKeys(loadedElements.map(() => newKey()));
        if (d.checkoutConfig) {
          setCheckoutConfig({
            courses: Array.isArray(d.checkoutConfig.courses)
              ? d.checkoutConfig.courses
              : Array.isArray(d.checkoutConfig.courses_ids)
              ? d.checkoutConfig.courses_ids
              : [],
            deep_reads: Array.isArray(d.checkoutConfig.deep_reads)
              ? d.checkoutConfig.deep_reads
              : Array.isArray(d.checkoutConfig.deepReads)
              ? d.checkoutConfig.deepReads
              : [],
            button_text: d.checkoutConfig.button_text || d.checkoutConfig.buttonText || "Lanjutkan Pembayaran",
            checkout_event: d.checkoutConfig.checkout_event || "AddPaymentInfo",
            page_view_event: d.checkoutConfig.page_view_event || "ViewContent",
          });
        }
      })
      .catch((e) => {
        setLoadingContent(false);
        if (e.response?.status === 404) {
          history.replace("/landing-page");
        }
      });
  };

  useEffect(() => {
    dispatch(
      setInterface({
        active: "Landing Page",
        pageTitle: editMode ? "Edit Landing Page" : "Tambah Landing Page",
        rightButton: null,
      })
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, editMode]);

  useEffect(() => {
    getCourses();
    getDeepReads();
    if (editMode) {
      loadPage();
    } else {
      setLoadingContent(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editMode, id]);

  const addElement = (type) => {
    const el = defaultElement(type);
    const key = newKey();
    setElements((prev) => [...prev, el]);
    setElementKeys((prev) => [...prev, key]);
    setExpandedKeys((prev) => new Set(prev).add(key));
    addModalRef.current?.hide();
  };

  const removeElement = (index) => {
    const key = elementKeys[index];
    setElements((prev) => prev.filter((_, i) => i !== index));
    setElementKeys((prev) => prev.filter((_, i) => i !== index));
    setExpandedKeys((prev) => {
      const next = new Set(prev);
      next.delete(key);
      return next;
    });
  };

  const reorderElement = (fromIndex, toIndex) => {
    if (fromIndex === toIndex || fromIndex == null || toIndex == null) return;
    if (toIndex < 0 || toIndex >= elements.length) return;
    setElements((prev) => {
      const next = [...prev];
      const [moved] = next.splice(fromIndex, 1);
      next.splice(toIndex, 0, moved);
      return next;
    });
    setElementKeys((prev) => {
      const next = [...prev];
      const [moved] = next.splice(fromIndex, 1);
      next.splice(toIndex, 0, moved);
      return next;
    });
  };

  const toggleExpand = (key) => {
    setExpandedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const updateElement = (index, patch) => {
    setElements((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], ...patch };
      return next;
    });
  };

  const handleDragStart = (index) => (e) => {
    setDragIndex(index);
    e.dataTransfer.effectAllowed = "move";
    try {
      e.dataTransfer.setData("text/plain", String(index));
    } catch (err) {
      // ignore browsers that restrict dataTransfer access
    }
  };

  const handleDragOver = (index) => (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    if (dragOverIndex !== index) setDragOverIndex(index);
  };

  const handleDrop = (index) => (e) => {
    e.preventDefault();
    if (dragIndex !== null) reorderElement(dragIndex, index);
    setDragIndex(null);
    setDragOverIndex(null);
  };

  const handleDragEnd = () => {
    setDragIndex(null);
    setDragOverIndex(null);
  };

  if (loadingContent) {
    return (
      <div className="fixed inset-0 z-40 bg-white-100 flex justify-center items-center flex-col">
        <Loader className="bg-black-100" />
        <span className="mt-3 text-black-300 text-sm">Memuat</span>
      </div>
    );
  }

  return (
    <Fragment>
      <Helmet>
        <title>{editMode ? "Edit Landing Page" : "Tambah Landing Page"}</title>
      </Helmet>
      <div className="fixed inset-0 z-40 bg-white-300 flex flex-col">
        {/* Header */}
        <header className="h-16 shrink-0 bg-white-100 border-b border-white-200 flex items-center px-4 gap-3 z-10">
          <button
            type="button"
            onClick={() => history.push("/landing-page")}
            className={"w-9 h-9 shrink-0 " + iconBtnClass}
            title="Kembali"
          >
            <FiX size={18} />
          </button>
          <div className="min-w-0 hidden sm:block">
            <div className="font-semibold text-sm text-black-200 truncate max-w-[220px]">
              {name || (editMode ? "Landing Page" : "Landing Page Baru")}
            </div>
            <div className="text-xs text-black-400 truncate max-w-[220px]">
              /pages/{slug || "-"}
            </div>
          </div>
          <span
            className={
              "px-2 py-1 rounded-sm text-xs font-semibold shrink-0 " +
              (status === "published"
                ? "bg-green-50 text-green-300"
                : "bg-white-200 text-black-300")
            }
          >
            {status === "published" ? "Published" : "Draft"}
          </span>

          <div className="flex-1" />

          <a
            href={slug ? frontendUrl(slug) : undefined}
            target="_blank"
            rel="noopener noreferrer"
            title={name || "Preview"}
            className={
              "h-10 px-3 rounded-sm text-sm font-semibold inline-flex items-center gap-1 border border-white-300 shrink-0 " +
              (slug
                ? "bg-white-100 hover:bg-white-200 text-black-200"
                : "opacity-50 pointer-events-none bg-white-100 text-black-300")
            }
          >
            <FiEye /> <span className="hidden md:inline">Preview</span>
          </a>

          <div className="relative shrink-0">
            <div className="flex">
              <Button
                type="button"
                color="green"
                noFixedHeight
                className="h-10 px-4 rounded-r-none"
                onClick={() => save("published")}
              >
                {status === "published" ? "Simpan Perubahan" : "Publish"}
              </Button>
              <button
                type="button"
                className="h-10 w-9 flex items-center justify-center rounded-sm rounded-l-none border-l border-green-300 bg-green-200 hover:bg-green-300 text-white-100"
                onClick={() => setPublishMenuOpen((v) => !v)}
                title="Opsi lainnya"
              >
                <FiChevronDown size={16} />
              </button>
            </div>
            {publishMenuOpen && (
              <Fragment>
                <div
                  className="fixed inset-0 z-20"
                  onClick={() => setPublishMenuOpen(false)}
                />
                <div className="absolute right-0 mt-1 w-48 bg-white-100 border border-white-200 rounded-sm shadow-popover py-1 z-30">
                  <button
                    type="button"
                    className="w-full text-left px-3 py-2 text-sm hover:bg-white-200 text-black-200"
                    onClick={() => {
                      setPublishMenuOpen(false);
                      save("draft");
                    }}
                  >
                    Save as Draft
                  </button>
                </div>
              </Fragment>
            )}
          </div>
        </header>

        {/* Body */}
        <div className="flex-1 flex overflow-hidden">
          {/* Left: tabbed panel (Elements / Halaman) */}
          <aside className="w-96 shrink-0 border-r border-white-200 bg-white-100 flex flex-col overflow-hidden">
            <div className="flex border-b border-white-200 shrink-0">
              <button
                type="button"
                onClick={() => setActiveTab("elements")}
                className={
                  "flex-1 h-11 flex items-center justify-center gap-1.5 text-xs font-semibold border-b-2 transition-colors " +
                  (activeTab === "elements"
                    ? "border-primary-500 text-primary-600"
                    : "border-transparent text-black-300 hover:text-black-200")
                }
              >
                <FiList size={14} /> Elements
              </button>
              <button
                type="button"
                onClick={() => setActiveTab("page")}
                className={
                  "flex-1 h-11 flex items-center justify-center gap-1.5 text-xs font-semibold border-b-2 transition-colors " +
                  (activeTab === "page"
                    ? "border-primary-500 text-primary-600"
                    : "border-transparent text-black-300 hover:text-black-200")
                }
              >
                <FiFileText size={14} /> Halaman
              </button>
            </div>

            {activeTab === "elements" && (
              <Fragment>
                <div className="p-4 border-b border-white-200 flex items-center justify-between shrink-0">
                  <span className="text-sm font-semibold text-black-200">Elements</span>
                  <button
                    type="button"
                    className="h-8 px-3 rounded-sm bg-primary-500 hover:bg-primary-600 text-white-100 text-xs font-semibold inline-flex items-center"
                    onClick={() => addModalRef.current?.show()}
                  >
                    <FiPlus className="mr-1" /> Tambah
                  </button>
                </div>
                <div className="flex-1 overflow-y-auto p-3 space-y-2">
                  {elements.length === 0 ? (
                    <p className="text-sm text-black-300 text-center py-6">
                      Belum ada element. Klik "Tambah" untuk mulai.
                    </p>
                  ) : (
                    elements.map((el, i) => (
                      <ElementItem
                        key={elementKeys[i]}
                        element={el}
                        index={i}
                        total={elements.length}
                        expanded={expandedKeys.has(elementKeys[i])}
                        dragging={dragIndex === i}
                        dragOver={dragOverIndex === i && dragIndex !== i}
                        onToggle={() => toggleExpand(elementKeys[i])}
                        onMoveUp={() => reorderElement(i, i - 1)}
                        onMoveDown={() => reorderElement(i, i + 1)}
                        onRemove={() => removeElement(i)}
                        onChange={(patch) => updateElement(i, patch)}
                        onDragStart={handleDragStart(i)}
                        onDragOver={handleDragOver(i)}
                        onDrop={handleDrop(i)}
                        onDragEnd={handleDragEnd}
                      />
                    ))
                  )}
                </div>
              </Fragment>
            )}

            {activeTab === "page" && (
              <div className="flex-1 overflow-y-auto p-4 space-y-4">
                <TextInput
                  label="Nama"
                  containerClassName="mb-1"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />
                <TextInput
                  label="Judul"
                  containerClassName="mb-1"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                />
                <TextInput
                  label="Slug"
                  containerClassName="mb-1"
                  value={slug}
                  onChange={(e) => setSlug(e.target.value)}
                  message="huruf kecil, angka, dan tanda strip"
                />
                <div>
                  <label className="text-black-200 font-semibold text-sm mb-1 block">
                    CAPI Event — Kunjungan Halaman
                  </label>
                  <select
                    value={checkoutConfig.page_view_event}
                    onChange={(e) =>
                      setCheckoutConfig({ ...checkoutConfig, page_view_event: e.target.value })
                    }
                    className="h-11 border border-white-300 w-full flex items-center rounded-sm bg-white-100 px-3 text-sm focus:outline-none"
                  >
                    {capiEvents.map((ev) => (
                      <option key={ev.value} value={ev.value}>
                        {ev.label}
                      </option>
                    ))}
                  </select>
                  <p className="text-xs text-black-400 mt-1">
                    Terkirim tiap kali halaman ini dibuka.
                  </p>
                </div>
                <div className="text-xs text-black-400 border-t border-white-200 pt-3">
                  Status halaman ({status === "published" ? "Published" : "Draft"}) diatur lewat
                  tombol Publish / Save as Draft di kanan atas.
                </div>
              </div>
            )}
          </aside>

          {/* Center: Live preview — matches the real public page 1:1 */}
          <main className="flex-1 overflow-auto">
            <LandingRendererWithCss page={previewPage} preview />
          </main>
        </div>
      </div>

      <Modal ref={addModalRef} title="Tambah Element">
        <div className="grid grid-cols-2 gap-3">
          {elementTypes.map((t) => {
            const Icon = t.icon;
            return (
              <button
                key={t.value}
                type="button"
                className="border border-white-300 rounded-sm p-4 text-left hover:border-primary-500 hover:bg-primary-50 transition-colors flex items-center gap-3"
                onClick={() => addElement(t.value)}
              >
                <span className="w-9 h-9 rounded-sm bg-white-200 flex items-center justify-center text-black-300 shrink-0">
                  <Icon size={16} />
                </span>
                <span className="font-semibold text-sm">{t.label}</span>
              </button>
            );
          })}
        </div>
      </Modal>

      {loading && <BigLoader />}
    </Fragment>
  );
}

function ElementItem({
  element,
  index,
  total,
  expanded,
  dragging,
  dragOver,
  onToggle,
  onMoveUp,
  onMoveDown,
  onRemove,
  onChange,
  onDragStart,
  onDragOver,
  onDrop,
  onDragEnd,
}) {
  const previewText = stripHtml(
    element.text || element.content || element.label || element.src || element.caption || ""
  );
  return (
    <div
      draggable
      onDragStart={onDragStart}
      onDragOver={onDragOver}
      onDrop={onDrop}
      onDragEnd={onDragEnd}
      className={
        "border rounded-sm bg-white-100 transition-colors " +
        (dragOver ? "border-primary-500 " : "border-white-300 ") +
        (dragging ? "opacity-40" : "")
      }
    >
      <div className="flex items-center gap-1.5 p-2 cursor-grab active:cursor-grabbing">
        <FiMove className="text-black-300 shrink-0" size={14} />
        <button
          type="button"
          className="flex-1 flex items-center justify-between text-left min-w-0 py-1"
          onClick={onToggle}
        >
          <span className="min-w-0">
            <span className="text-sm font-semibold capitalize block truncate">
              {elementTypeLabel(element.type)}
            </span>
            {previewText && (
              <span className="text-xs text-black-400 block truncate">{previewText}</span>
            )}
          </span>
          {expanded ? (
            <FiChevronUp className="shrink-0 ml-2 text-black-300" size={14} />
          ) : (
            <FiChevronDown className="shrink-0 ml-2 text-black-300" size={14} />
          )}
        </button>
        <div className="flex items-center gap-0.5 shrink-0" onClick={(e) => e.stopPropagation()}>
          <button
            type="button"
            className={"w-7 h-7 " + iconBtnClass}
            disabled={index === 0}
            onClick={onMoveUp}
          >
            <FiArrowUp size={13} />
          </button>
          <button
            type="button"
            className={"w-7 h-7 " + iconBtnClass}
            disabled={index === total - 1}
            onClick={onMoveDown}
          >
            <FiArrowDown size={13} />
          </button>
          <button
            type="button"
            className={"w-7 h-7 " + dangerIconBtnClass}
            onClick={onRemove}
          >
            <FiTrash size={13} />
          </button>
        </div>
      </div>
      {expanded && (
        <div className="border-t border-white-200 p-3">
          <ElementEditor
            element={element}
            onChange={onChange}
          />
        </div>
      )}
    </div>
  );
}

function ElementEditor({
  element,
  onChange,
}) {
  const type = element.type;
  if (type === "heading") {
    return (
      <Fragment>
        <TextInput
          label="Teks"
          containerClassName="mb-4"
          value={element.text || ""}
          onChange={(e) => onChange({ text: e.target.value })}
        />
        <div>
          <label className="text-black-200 font-semibold text-sm mb-1 block">Level</label>
          <select
            value={element.level || 2}
            onChange={(e) => onChange({ level: Number(e.target.value) })}
            className="h-11 border border-white-300 w-full flex items-center rounded-sm bg-white-100 px-3 text-sm focus:outline-none"
          >
            {[1, 2, 3, 4, 5, 6].map((l) => (
              <option key={l} value={l}>
                H{l}
              </option>
            ))}
          </select>
        </div>
      </Fragment>
    );
  }
  if (type === "paragraph") {
    return (
      <Editor
        label="Konten"
        value={element.content || ""}
        onChange={(val) => onChange({ content: val })}
      />
    );
  }
  if (type === "image") {
    return (
      <Fragment>
        <TextInput
          label="URL Gambar"
          containerClassName="mb-4"
          value={element.src || ""}
          onChange={(e) => onChange({ src: e.target.value })}
          placeholder="https://..."
        />
        <TextInput
          label="Alt Text"
          containerClassName="mb-4"
          value={element.alt || ""}
          onChange={(e) => onChange({ alt: e.target.value })}
        />
        <TextInput
          label="Caption (opsional)"
          value={element.caption || ""}
          onChange={(e) => onChange({ caption: e.target.value })}
        />
        {element.src ? (
          <img
            src={element.src}
            alt={element.alt || ""}
            className="mt-4 w-full rounded-sm border border-white-200"
          />
        ) : null}
      </Fragment>
    );
  }
  if (type === "testimonials") {
    const items = element.items || [];
    const updateItem = (i, patch) => {
      const next = [...items];
      next[i] = { ...next[i], ...patch };
      onChange({ items: next });
    };
    return (
      <Fragment>
        {items.map((item, i) => (
          <div key={i} className="border border-white-200 rounded-sm p-3 mb-3">
            <TextInput
              label="Nama"
              containerClassName="mb-2"
              value={item.name || ""}
              onChange={(e) => updateItem(i, { name: e.target.value })}
            />
            <Textarea
              label="Testimoni"
              rows={3}
              value={item.text || ""}
              onChange={(e) => updateItem(i, { text: e.target.value })}
            />
            <button
              type="button"
              className="mt-2 text-sm text-red-300 hover:underline"
              onClick={() => onChange({ items: items.filter((_, idx) => idx !== i) })}
            >
              Hapus
            </button>
          </div>
        ))}
        <Button
          type="button"
          color="white"
          onClick={() => onChange({ items: [...items, { name: "", text: "" }] })}
        >
          <FiPlus className="mr-1" /> Tambah Testimoni
        </Button>
      </Fragment>
    );
  }
  if (type === "button") {
    return (
      <Fragment>
        <TextInput
          label="Label"
          containerClassName="mb-4"
          value={element.label || ""}
          onChange={(e) => onChange({ label: e.target.value })}
        />
        <TextInput
          label="URL"
          containerClassName="mb-4"
          value={element.url || ""}
          onChange={(e) => onChange({ url: e.target.value })}
          placeholder="#checkout atau https://..."
        />
        <div>
          <label className="text-black-200 font-semibold text-sm mb-1 block">Style</label>
          <select
            value={element.style || "dark"}
            onChange={(e) => onChange({ style: e.target.value })}
            className="h-11 border border-white-300 w-full flex items-center rounded-sm bg-white-100 px-3 text-sm focus:outline-none"
          >
            {buttonStyles.map((s) => (
              <option key={s.value} value={s.value}>
                {s.label}
              </option>
            ))}
          </select>
        </div>
      </Fragment>
    );
  }
  if (type === "list") {
    const items = element.items || [];
    const updateItem = (i, val) => {
      const next = [...items];
      next[i] = { text: val };
      onChange({ items: next });
    };
    return (
      <Fragment>
        <div className="mb-4">
          <label className="text-black-200 font-semibold text-sm mb-1 block">Tipe List</label>
          <select
            value={element.ordered ? "ol" : "ul"}
            onChange={(e) => onChange({ ordered: e.target.value === "ol" })}
            className="h-11 border border-white-300 w-full flex items-center rounded-sm bg-white-100 px-3 text-sm focus:outline-none"
          >
            <option value="ul">Bullet (ul)</option>
            <option value="ol">Numbered (ol)</option>
          </select>
        </div>
        {items.map((item, i) => (
          <div key={i} className="flex items-center gap-2 mb-2">
            <TextInput
              className="flex-1"
              value={item.text || ""}
              onChange={(e) => updateItem(i, e.target.value)}
              placeholder={`Item ${i + 1}`}
            />
            <button
              type="button"
              className={"w-9 h-9 " + dangerIconBtnClass}
              onClick={() => onChange({ items: items.filter((_, idx) => idx !== i) })}
            >
              <FiTrash size={14} />
            </button>
          </div>
        ))}
        <Button
          type="button"
          color="white"
          onClick={() => onChange({ items: [...items, { text: "" }] })}
        >
          <FiPlus className="mr-1" /> Tambah Item
        </Button>
      </Fragment>
    );
  }
  return null;
}

function LandingRendererWithCss({ page, preview }) {
  return (
    <Fragment>
      <style>{LANDING_CSS}</style>
      <LandingRenderer page={page} preview={preview} />
    </Fragment>
  );
}
